import { Injectable } from "@angular/core";
import {BehaviorSubject, Observable, Subject} from "rxjs";
import {
    BOT_MODE,
    BotMode,
    ChatCommands,
    ChatCommandsEnum,
    GreetingHelpBotMessage,
    GreetingTSeBotMessage,
    FollowBackAiInstructionEnum,
    FollowBackState, FollowBackStateList,
} from "../models/chat-commands.model";
import { AiInsDetectionResponse } from "../../../components/Shared/voice-instructions/Models/ai-model.model";
import { v4 as uuidv4 } from 'uuid';

@Injectable({
    providedIn: "root",
})
export class ChatbotService {
    private chatbotOpenSubject = new BehaviorSubject<boolean>(false);
    chatbotOpen$ = this.chatbotOpenSubject.asObservable();

    private botModeSubject = new BehaviorSubject<BotMode>(BOT_MODE.TSE as BotMode);
    botMode$ = this.botModeSubject.asObservable();

    private followBackSubject = new BehaviorSubject<FollowBackState | null>(null);
    followBack$ = this.followBackSubject.asObservable();

    private listFollowBackSubject = new BehaviorSubject<FollowBackStateList | null>(null);
    listFollowBack$ = this.listFollowBackSubject.asObservable();

    private followBackStartSubject = new Subject<'chat' | 'voice'>();
    followBackStart$ = this.followBackStartSubject.asObservable();

    private partialResponse: { [key in FollowBackAiInstructionEnum]?: string } = {};

    private sessionIds: { [key in BotMode]: string } = {
        TSE: '',
        INFO: uuidv4(), // Generate initial UUID for INFO bot
    };

    private chatHistories: { [key in BotMode]: ChatCommands[] } = {
        TSE: [],
        INFO: [],
    };

    private messagesSubject = new BehaviorSubject<{ [key in BotMode]: ChatCommands[] }>({
        TSE: [],
        INFO: [],
    });
    messages$ = this.messagesSubject.asObservable();
    hasMessagesSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    hasMessages$: Observable<boolean> = this.hasMessagesSubject.asObservable();

    constructor() {}

    toggleChatbot(): void {
        this.chatbotOpenSubject.next(!this.chatbotOpenSubject.value);
    }

    closeChatbot(): void {
        this.chatbotOpenSubject.next(false);
    }

    openChatbot(): void {
        this.chatbotOpenSubject.next(true);
    }

    checkIsHasMessages(): void {
        const current = this.messagesSubject.value;
        const mode = this.botModeSubject.value;
        const hasMessages = current[mode] && current[mode].length > 1;
        this.hasMessagesSubject.next(hasMessages);
    }

    setBotMode(mode: BotMode): void {
        this.botModeSubject.next(mode);
        this.checkIsHasMessages();
        this.ensureGreetingMessage(mode);
        this.clearFollowBack();

        // set session id for INFO Bot if not already set
        if (mode === BOT_MODE.INFO && !this.sessionIds[BOT_MODE.INFO]) {
            this.sessionIds[BOT_MODE.INFO] = uuidv4();
        }
    }

    getBotMode(): BotMode {
        return this.botModeSubject.value;
    }

    get currentSessionId(): string {
        return this.sessionIds[this.getBotMode()];
    }

    setFollowBack(type: FollowBackAiInstructionEnum, context: AiInsDetectionResponse) {
        this.followBackSubject.next({ isActive: true, type, context });
    }

    setListFollowBack(type: FollowBackAiInstructionEnum[], context: AiInsDetectionResponse) {
        this.listFollowBackSubject.next({ isActive: true, type, context });
    }

    clearFollowBack() {
        this.followBackSubject.next(null);
        this.listFollowBackSubject.next(null);
        this.clearPartialResponse()
    }

    get isFollowBackActive(): boolean {
        return !!this.followBackSubject.value?.isActive;
    }

    get followBackType(): FollowBackAiInstructionEnum | null {
        return this.followBackSubject.value?.type != null ? this.followBackSubject.value.type : null;
    }

    get activeAiContext(): AiInsDetectionResponse | null {
        return this.followBackSubject.value?.context || null;
    }

    resetCurrentBot() {
        const mode = this.botModeSubject.value;
        const greeting = this.getInitialMessage(mode);
        this.chatHistories[mode] = [greeting];
        this.messagesSubject.next({ ...this.chatHistories });
        this.checkIsHasMessages();
        this.clearFollowBack();

        // reset sessionId
        if (mode === BOT_MODE.INFO) {
            this.sessionIds[BOT_MODE.INFO] = uuidv4();
        }
    }

    private getInitialMessage(mode: BotMode): ChatCommands {
        return new ChatCommands(
            mode === BOT_MODE.TSE ? GreetingTSeBotMessage : GreetingHelpBotMessage,
            ChatCommandsEnum.aiInformationalMessage,
        );
    }

    ensureGreetingMessage(mode: BotMode): void {
        const current = this.messagesSubject.value;
        if (!current[mode] || current[mode].length === 0) {
            const initialMessage = this.getInitialMessage(mode);

            const updated = { ...current, [mode]: [initialMessage] };
            this.messagesSubject.next(updated);
        }
    }

    getPlaceholder(mode: BotMode) {
        return mode === BOT_MODE.TSE
            ? "i.e Create Work Item with Title Onboarding on Vijayi’s app which starts today and ends tomorrow. It has an estimate of 4 hours."
            : "i.e How do I create a work item? ";
    }

    addMessage(botMode: BotMode, message: ChatCommands): void {
        // Append the new message to the appropriate chat history
        this.chatHistories[botMode] = [...this.chatHistories[botMode], message];

        // Emit the updated chat histories
        this.messagesSubject.next(this.chatHistories);
        this.checkIsHasMessages();
    }
    get getFollowTypeList(): FollowBackAiInstructionEnum[] | null {
        return this.listFollowBackSubject.value != null ? this.listFollowBackSubject.value.type : null;
    }
    get isFollowBackListActive(): boolean {
        return !!this.listFollowBackSubject.value?.isActive;
    }
    get isFollowBackListAiResponse() {
        return this.listFollowBackSubject.value?.context;
    }

    setPartialResponse(type: FollowBackAiInstructionEnum, value: string) {
        this.partialResponse[type] = value;
    }

    getPartialResponse(): { [key in FollowBackAiInstructionEnum]?: string } {
        return this.partialResponse;
    }

    clearPartialResponse() {
        this.partialResponse = {};
    }

    startFollowBack(source: 'chat' | 'voice') {
        this.followBackStartSubject.next(source);
    }
}
